/**
 * @param {string} exchangeId
 * @param {Map<number, { user?: import('./chatThreadTypes.js').ChatMessage, assistants: Map<number, import('./chatThreadTypes.js').ChatMessage> }>} queryVersions
 * @param {Record<string, number>} activeQueryVersionByExchangeId
 */
export function getQueryVersionNavState(exchangeId, queryVersions, activeQueryVersionByExchangeId) {
  const queryVersionIndices = [...queryVersions.keys()]
    .filter((idx) => Boolean(queryVersions.get(idx)?.user))
    .sort((a, b) => a - b)

  const maxQueryVersionIndex = queryVersionIndices.length
    ? queryVersionIndices[queryVersionIndices.length - 1]
    : 0

  const activeQueryVersionIndex =
    typeof activeQueryVersionByExchangeId[exchangeId] === 'number'
      ? activeQueryVersionByExchangeId[exchangeId]
      : maxQueryVersionIndex

  const activeQuery = queryVersions.get(activeQueryVersionIndex) ?? queryVersions.get(maxQueryVersionIndex)
  const currentUser = activeQuery?.user ?? null
  // No user is a real, renderable exchange: a scheduled workflow posts its
  // result with nothing asked in front of it. Only a genuinely empty exchange —
  // no question *and* no answer — has nothing to draw, so that is the one case
  // that still refuses the row. Returning null for the user-less kind is what
  // made the workflow updates invisible while still occupying a virtual row.
  if (!currentUser) {
    // Found by scan rather than by key: `maxQueryVersionIndex` is derived from
    // the user-bearing versions, which is exactly the empty set here, so it
    // cannot be trusted to name a real entry.
    const entry = [...queryVersions.entries()].find(([, qv]) => qv?.assistants?.size)
    if (!entry) return null
    const [answerVersionIndex, fallback] = entry
    return {
      activeQuery: fallback,
      activeQueryVersionIndex: answerVersionIndex,
      currentUser: null,
      // There is no question to page between, so the query pager is inert.
      queryActiveOrdinal: 0,
      queryCount: 1,
      queryCanPrev: false,
      queryCanNext: false,
      queryPrevVersionIndex: answerVersionIndex,
      queryNextVersionIndex: answerVersionIndex,
    }
  }

  const queryActiveOrdinal = Math.max(0, queryVersionIndices.indexOf(activeQueryVersionIndex))
  const queryCount = queryVersionIndices.length || 1

  return {
    activeQuery,
    activeQueryVersionIndex,
    currentUser,
    queryActiveOrdinal,
    queryCount,
    queryCanPrev: queryActiveOrdinal > 0,
    queryCanNext: queryActiveOrdinal < queryCount - 1,
    queryPrevVersionIndex: queryActiveOrdinal > 0
      ? queryVersionIndices[queryActiveOrdinal - 1]
      : activeQueryVersionIndex,
    queryNextVersionIndex: queryActiveOrdinal < queryCount - 1
      ? queryVersionIndices[queryActiveOrdinal + 1]
      : activeQueryVersionIndex,
  }
}
